"use strict";

import { PCA_BRIDGE_BUS_INIT } from "./bus_init.js";
import { DisposedError, HandlerError, TimeoutError } from "./errors.js";
import { generateId, noopLogger, parseWireEnvelope, shallowEqual } from "./util.js";
import { BRIDGE_VERSION } from "./version.js";
const DEFAULT_TIMEOUT_MS = 5000;
const INCOMING_CALL_QUEUE_TTL_MS = 5000;
const INCOMING_CALL_QUEUE_MAX_PER_ACTION = 32;
export class BridgeCore {
  injectedJavaScriptBeforeContentLoaded = PCA_BRIDGE_BUS_INIT;
  localVersion = BRIDGE_VERSION;
  // `never` arg type lets us widen any user-defined `LocalActions[K]` callable
  // with a single `as` cast inside `expose()`.
  localActions = new Map();
  localValues = new Map();
  remoteActions = new Set();
  remoteValues = new Map();
  actionListeners = new Map();
  valueListeners = new Map();
  pendingCalls = new Map();
  callQueue = new Map();

  // Private backing field + public getter so the `readonly` declared on the
  // Bridge interface is actually enforced — not just at the interface level
  // but for any code holding a concrete BridgeCore reference.
  _remoteVersion = undefined;
  get remoteVersion() {
    return this._remoteVersion;
  }
  disposed = false;
  constructor(options) {
    const sendRaw = options.send;
    this.send = msg => {
      sendRaw({
        ...msg,
        bridgeVersion: BRIDGE_VERSION
      });
    };
    this.logger = options.logger ?? noopLogger;
    this.defaultTimeoutMs = options.defaultTimeoutMs ?? DEFAULT_TIMEOUT_MS;
    this.now = options.now ?? Date.now;
  }
  call(name, args, options) {
    const requestId = generateId();
    const timeoutMs = options?.timeoutMs ?? this.defaultTimeoutMs;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pendingCalls.delete(requestId)) {
          reject(new TimeoutError(name, timeoutMs));
        }
      }, timeoutMs);
      // Heterogeneous map: cast the typed resolve to the loose storage slot.
      this.pendingCalls.set(requestId, {
        name,
        resolve: resolve,
        reject,
        timer
      });
      this.send({
        type: '__bridge/call',
        requestId,
        name,
        args
      });
    });
  }
  expose(name, handler) {
    if (this.localActions.has(name)) {
      this.logger.warn('Action already exposed; last-write-wins (the previous handler is gone)', {
        name
      });
    }
    this.localActions.set(name, handler);
    this.send({
      type: '__bridge/exposed',
      name,
      kind: 'action'
    });
    const queued = this.callQueue.get(name);
    if (queued) {
      this.callQueue.delete(name);
      const cutoff = this.now() - INCOMING_CALL_QUEUE_TTL_MS;
      for (const entry of queued) {
        if (entry.receivedAt >= cutoff) {
          void this.invokeLocalHandler(entry.msg);
        } else {
          this.logger.warn('Queued incoming call expired before handler registered', {
            name,
            ageMs: this.now() - entry.receivedAt
          });
          this.rejectQueued(entry, 'expired');
        }
      }
    }
  }
  unexpose(name) {
    if (this.localActions.delete(name)) {
      this.send({
        type: '__bridge/unexposed',
        name,
        kind: 'action'
      });
    }
  }
  hasRemoteAction(name) {
    return this.remoteActions.has(name);
  }
  onRemoteAction(name, callback) {
    let set = this.actionListeners.get(name);
    if (!set) {
      set = new Set();
      this.actionListeners.set(name, set);
    }
    set.add(callback);
    return () => {
      const current = this.actionListeners.get(name);
      if (current) {
        current.delete(callback);
        if (current.size === 0) {
          this.actionListeners.delete(name);
        }
      }
    };
  }
  publish(name, value) {
    // Skip wire send if content unchanged — callers may publish on every
    // render with a fresh-but-content-equal value.
    if (!shallowEqual(this.localValues.get(name), value)) {
      this.localValues.set(name, value);
      this.send({
        type: '__bridge/publish',
        name,
        value
      });
    }
  }
  unpublish(name) {
    if (this.localValues.delete(name)) {
      this.send({
        type: '__bridge/unexposed',
        name,
        kind: 'value'
      });
    }
  }
  seedLocalValue(name, value) {
    this.localValues.set(name, value);
  }
  getRemoteValue(name) {
    return this.remoteValues.get(name);
  }
  onRemoteValue(name, callback) {
    let set = this.valueListeners.get(name);
    if (!set) {
      set = new Set();
      this.valueListeners.set(name, set);
    }
    const wrapped = callback;
    set.add(wrapped);
    return () => {
      const current = this.valueListeners.get(name);
      if (current) {
        current.delete(wrapped);
        if (current.size === 0) {
          this.valueListeners.delete(name);
        }
      }
    };
  }
  reannounceLocal() {
    for (const name of this.localActions.keys()) {
      this.send({
        type: '__bridge/exposed',
        name,
        kind: 'action'
      });
    }
    for (const [name, value] of this.localValues) {
      this.send({
        type: '__bridge/exposed',
        name,
        kind: 'value',
        currentValue: value
      });
    }
  }

  /**
   * Drop remote-side cached state. Called by transports when the remote JS
   * context is about to be torn down (e.g., WebView reload): the new context
   * will re-announce what it actually exposes, and stale entries would
   * otherwise silently mask removed actions/values.
   */
  clearRemoteState() {
    for (const name of this.remoteActions) {
      this.notifySet(this.actionListeners, name, false, 'action availability');
    }
    this.remoteActions.clear();
    for (const name of this.remoteValues.keys()) {
      this.notifySet(this.valueListeners, name, undefined, 'remote value change');
    }
    this.remoteValues.clear();
    this._remoteVersion = undefined;
  }
  handleMessage(rawData) {
    const msg = parseWireEnvelope(rawData);
    let consumed = false;
    if (msg) {
      this.handleIncomingMessage(msg);
      consumed = true;
    }
    return consumed;
  }
  applyLogger(logger) {
    if (this.logger === noopLogger) {
      this.logger = logger;
      return true;
    }
    return false;
  }
  handleLoadStart() {}
  handleLoadEnd() {}
  handleNavigationStateChange(navState) {
    if (!navState.loading) {
      this.handleLoadEnd();
    }
  }
  handleIncomingMessage(msg) {
    if (!this.disposed) {
      this._remoteVersion = msg.bridgeVersion;
      switch (msg.type) {
        case '__bridge/call':
          void this.routeIncomingCall(msg);
          break;
        case '__bridge/response':
          this.handleIncomingResponse(msg);
          break;
        case '__bridge/exposed':
          this.handleIncomingExposed(msg);
          break;
        case '__bridge/unexposed':
          this.handleIncomingUnexposed(msg);
          break;
        case '__bridge/publish':
          this.handleIncomingPublish(msg);
          break;
        case '__bridge/busReady':
        case '__bridge/drop':
          break;
        default:
          {
            // parseWireEnvelope admits any `__bridge/*` type; this catches
            // future message types a remote on a newer bridge sends to us.
            const unknown = msg;
            this.logger.warn('Received unknown bridge message type; ignoring', {
              type: unknown.type,
              remoteVersion: this.remoteVersion
            });
            break;
          }
      }
    }
  }
  get debug() {
    return {
      listLocalActions: () => Array.from(this.localActions.keys()),
      listRemoteActions: () => Array.from(this.remoteActions),
      listLocalValues: () => Array.from(this.localValues.keys()),
      listRemoteValues: () => Array.from(this.remoteValues.keys())
    };
  }
  dispose() {
    if (!this.disposed) {
      this.disposed = true;
      for (const pending of this.pendingCalls.values()) {
        clearTimeout(pending.timer);
        pending.reject(new DisposedError(pending.name));
      }
      this.pendingCalls.clear();
      this.callQueue.clear();
    }
  }
  async routeIncomingCall(msg) {
    if (this.localActions.has(msg.name)) {
      await this.invokeLocalHandler(msg);
      return;
    }
    let queued = this.callQueue.get(msg.name);
    if (!queued) {
      queued = [];
      this.callQueue.set(msg.name, queued);
    }
    // Evict TTL-expired and over-capacity entries so a remote that spams an
    // unknown action can't grow the queue without bound. Each evicted call
    // gets an error response so the caller fails fast.
    const cutoff = this.now() - INCOMING_CALL_QUEUE_TTL_MS;
    while (queued.length > 0) {
      const head = queued[0];
      if (!head || head.receivedAt >= cutoff) break;
      queued.shift();
      this.rejectQueued(head, 'expired');
    }
    while (queued.length >= INCOMING_CALL_QUEUE_MAX_PER_ACTION) {
      const evicted = queued.shift();
      if (!evicted) break;
      this.rejectQueued(evicted, 'queue_full');
    }
    queued.push({
      msg,
      receivedAt: this.now()
    });
  }
  rejectQueued(entry, code) {
    this.send({
      type: '__bridge/response',
      requestId: entry.msg.requestId,
      ok: false,
      code,
      message: `Queued call to "${entry.msg.name}" dropped (${code})`
    });
  }
  async invokeLocalHandler(msg) {
    // Handler may have been unregistered between queue push and drain. Reply
    // with an explicit error so the caller fails fast instead of timing out.
    const handler = this.localActions.get(msg.name);
    if (!handler) {
      this.send({
        type: '__bridge/response',
        requestId: msg.requestId,
        ok: false,
        code: 'unexposed',
        message: `No handler exposed for "${msg.name}"`
      });
      return;
    }
    try {
      const result = await handler(msg.args);
      this.send({
        type: '__bridge/response',
        requestId: msg.requestId,
        ok: true,
        result
      });
    } catch (error) {
      this.send({
        type: '__bridge/response',
        requestId: msg.requestId,
        ok: false,
        code: 'thrown',
        message: error instanceof Error ? error.message : String(error)
      });
    }
  }
  handleIncomingResponse(msg) {
    const pending = this.pendingCalls.get(msg.requestId);
    if (pending) {
      this.pendingCalls.delete(msg.requestId);
      clearTimeout(pending.timer);
      if (msg.ok) {
        pending.resolve(msg.result);
      } else {
        pending.reject(new HandlerError(pending.name, msg.code, msg.message));
      }
    } else {
      this.logger.warn('Response for unknown or already-resolved requestId', {
        requestId: msg.requestId
      });
    }
  }
  handleIncomingExposed(msg) {
    if (msg.kind === 'action') {
      if (!this.remoteActions.has(msg.name)) {
        this.remoteActions.add(msg.name);
        this.notifySet(this.actionListeners, msg.name, true, 'action availability');
      }
    } else {
      this.remoteValues.set(msg.name, msg.currentValue);
      this.notifySet(this.valueListeners, msg.name, msg.currentValue, 'remote value change');
    }
  }
  handleIncomingUnexposed(msg) {
    if (msg.kind === 'action') {
      if (this.remoteActions.delete(msg.name)) {
        this.notifySet(this.actionListeners, msg.name, false, 'action availability');
      }
    } else if (this.remoteValues.delete(msg.name)) {
      this.notifySet(this.valueListeners, msg.name, undefined, 'remote value change');
    }
  }
  handleIncomingPublish(msg) {
    this.remoteValues.set(msg.name, msg.value);
    this.notifySet(this.valueListeners, msg.name, msg.value, 'remote value change');
  }
  notifySet(subs, name, value, label) {
    const set = subs.get(name);
    if (set) {
      for (const callback of set) {
        try {
          callback(value);
        } catch (error) {
          this.logger.error(`Subscriber threw during ${label} notification`, {
            name,
            error
          });
        }
      }
    }
  }
}
//# sourceMappingURL=bridge_core.js.map